import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Icon } from "@iconify/react";
import AddUpdateModal from "../../components/formComponents/addUpdateModal";
import AddPromoCode from "./components/addPromoCode";
import toast from "react-hot-toast";
import Common from "../../helper/common";
import { respEnum } from "../../constants/appConstant";

export default function PromoCode() {
  const [addVisible, setAddVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load promocodes data
  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    setLoading(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetPromoCodes",
        null,
        "GET"
      );

      if (response && response.status === respEnum.Success) {
        setPromoCodes(response.data || []);
      } else {
        toast.error("Failed to load promocodes");
        setPromoCodes([]);
      }
    } catch (error) {
      toast.error("Failed to load promocodes");
      setPromoCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promoCode) => {
    setEditData(promoCode);
    setEditVisible(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this promocode?")) {
      try {
        const response = await Common.ApiService.getInstance().request(
          `DeletePromoCode?id=${id}`,
          null,
          "POST"
        );

        if (response && response.status === respEnum.Success) {
          toast.success("PromoCode deleted successfully");
          loadPromoCodes();
        } else {
          toast.error("Failed to delete promocode");
        }
      } catch (error) {
        toast.error("Failed to delete promocode");
      }
    }
  };

  const toggleActive = async (id, isActive) => {
    try {
      const response = await Common.ApiService.getInstance().request(
        `ActiveDeactivePromoCode?id=${id}&isactive=${isActive}`,
        null,
        "POST"
      );

      if (response && response.status === respEnum.Success) {
        toast.success(
          `PromoCode ${isActive ? "activated" : "deactivated"} successfully`
        );
        loadPromoCodes();
      } else {
        toast.error("Failed to update promocode status");
      }
    } catch (error) {
      toast.error("Failed to update promocode status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDiscountTypeText = (type) => {
    return type === 1 ? "%" : "Fixed Amount";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Promo Codes</h1>
        <Button
          className="h-8 text-xs"
          size="xs"
          variant=""
          onClick={setAddVisible.bind(this, true)}
        >
          <span className="text-xl mr-1">
            <Icon icon="icon-park-outline:plus" />
          </span>
          Add Promo Code
        </Button>
      </div>

      {/* PromoCodes List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sport
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Public
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : promoCodes.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No promocodes found
                  </td>
                </tr>
              ) : (
                promoCodes.map((promoCode) => (
                  <tr key={promoCode.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {promoCode.code}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {promoCode.discountValue}{" "}
                        {getDiscountTypeText(promoCode.discountType)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {promoCode.sportName || "All Sports"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          promoCode.isPublic
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {promoCode.isPublic ? "Public" : "Private"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(promoCode.validFrom)} -{" "}
                        {formatDate(promoCode.validTo)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {promoCode.totalRedeemed} /{" "}
                        {promoCode.usageLimit || "∞"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          promoCode.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {promoCode.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(promoCode)}
                        >
                          <Icon
                            icon="icon-park-outline:edit"
                            className="w-4 h-4"
                          />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            toggleActive(promoCode.id, !promoCode.isActive)
                          }
                        >
                          <Icon
                            icon={
                              promoCode.isActive
                                ? "icon-park-outline:close"
                                : "icon-park-outline:check"
                            }
                            className="w-4 h-4"
                          />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(promoCode.id)}
                        >
                          <Icon
                            icon="icon-park-outline:delete"
                            className="w-4 h-4"
                          />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      <AddUpdateModal
        open={addVisible}
        setOpen={setAddVisible}
        Component={(props) => (
          <AddPromoCode
            {...props}
            onRequestClose={() => {
              setAddVisible(false);
              loadPromoCodes();
            }}
          />
        )}
        title={"Add Promo Code"}
      />

      {/* Edit Modal */}
      <AddUpdateModal
        open={editVisible}
        setOpen={setEditVisible}
        Component={(props) => (
          <AddPromoCode
            {...props}
            editData={editData}
            onRequestClose={() => {
              setEditVisible(false);
              setEditData(null);
              loadPromoCodes();
            }}
          />
        )}
        title={"Edit Promo Code"}
      />
    </div>
  );
}
